import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import Header from "./Header";
import Footer from "./Footer";

import config from "../../config";

const Shipping_rules = () => {
  // ============================================================
  // USER
  // ============================================================

  const userDetail = localStorage.getItem("ship_rocket_user");

  let userData = {};

  if (userDetail) {
    try {
      userData = JSON.parse(userDetail);
    } catch (error) {
      console.error("Invalid user data:", error);
    }
  }

  // ============================================================
  // STATE
  // ============================================================

  const [rules, setRules] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [activeTab, setActiveTab] = useState("all");

  const [draggedRuleId, setDraggedRuleId] = useState(null);

  const [dropTargetId, setDropTargetId] = useState(null);

  const [savingOrder, setSavingOrder] = useState(false);

  const [updatingStatus, setUpdatingStatus] = useState(null);

  const [deletingRule, setDeletingRule] = useState(null);

  const [duplicatingRule, setDuplicatingRule] = useState(null);

  // ============================================================
  // HELPERS
  // ============================================================

  const typeClass = {
    Domestic: "domestic",
    International: "international",
    COD: "cod",
    Prepaid: "prepaid",
    B2B: "b2b",
    Custom: "custom",
  };

  const actionLabel = {
    custom: "Custom",
    fastest: "Fastest",
    cheapest: "Cheapest",
  };

  // ============================================================
  // RELATIVE TIME
  // ============================================================

  const relTime = (dateValue) => {
    if (!dateValue) {
      return "—";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    const now = new Date();

    const diffMs = now.getTime() - date.getTime();

    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) {
      return "Just now";
    }

    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);

    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }

    const diffDays = Math.floor(diffHours / 24);

    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }

    const diffWeeks = Math.floor(diffDays / 7);

    if (diffWeeks < 5) {
      return `${diffWeeks}w ago`;
    }

    const diffMonths = Math.floor(diffDays / 30);

    if (diffMonths < 12) {
      return `${diffMonths}mo ago`;
    }

    const diffYears = Math.floor(diffDays / 365);

    return `${diffYears}y ago`;
  };

  // ============================================================
  // FETCH RULES
  // ============================================================

  const loadRules = async () => {
    try {
      if (!userData?.customer_id) {
        console.error("Customer ID not found.");

        setRules([]);

        return;
      }

      setLoading(true);

      const response = await axios.post(
        config.apiUrl + "/shipping_rule/list",
        {
          customer_id: userData.customer_id,
        },
        {
          headers: config.headers,
        }
      );

      console.log("Shipping Rules List Response =>", response.data);

      if (response.data?.status === true) {
        const output = Array.isArray(response.data.output)
          ? response.data.output
          : [];

        /*
         * Always sort by evaluation_order.
         *
         * This is important because the order in MongoDB
         * controls the order in which rules are evaluated.
         */
        const sortedRules = [...output].sort((a, b) => {
          const orderA = Number(a?.evaluation_order) || 999999;
          const orderB = Number(b?.evaluation_order) || 999999;

          return orderA - orderB;
        });

        setRules(sortedRules);
      } else {
        setRules([]);

        console.error(
          response.data?.message || "Unable to fetch shipping rules."
        );
      }
    } catch (error) {
      console.error(
        "Shipping Rules List API Error =>",
        error?.response?.data || error
      );

      setRules([]);

      Swal.fire({
        icon: "error",
        title: "Unable to load shipping rules",
        text:
          error?.response?.data?.message ||
          "Something went wrong while loading shipping rules.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    loadRules();
  }, []);

  // ============================================================
  // COUNTS
  // ============================================================

  const allCount = rules.length;

  const activeCount = rules.filter(
    (item) => String(item?.status || "").toLowerCase() === "active"
  ).length;

  const inactiveCount = rules.filter(
    (item) => String(item?.status || "").toLowerCase() === "inactive"
  ).length;

  // ============================================================
  // FILTERED RULES
  // ============================================================

  const visibleRules = useMemo(() => {
    let result = [...rules];

    // ----------------------------------------------------------
    // TAB FILTER
    // ----------------------------------------------------------

    if (activeTab === "active") {
      result = result.filter(
        (item) => String(item?.status || "").toLowerCase() === "active"
      );
    }

    if (activeTab === "inactive") {
      result = result.filter(
        (item) => String(item?.status || "").toLowerCase() === "inactive"
      );
    }

    // ----------------------------------------------------------
    // SEARCH
    // ----------------------------------------------------------

    const query = search.trim().toLowerCase();

    if (query) {
      result = result.filter((rule) => {
        const conditionNames = Array.isArray(rule?.conditions)
          ? rule.conditions.map((condition) => {
              switch (condition?.type) {
                case "payment":
                  return "payment mode";

                case "weight":
                  return "weight";

                case "orderValue":
                  return "order value";

                case "pincode":
                  return "pincode";

                case "zone":
                  return "zone";

                case "state":
                  return "state";

                case "sku":
                  return "product sku";

                default:
                  return condition?.type || "";
              }
            })
          : [];

        const searchable = [
          rule?.name || "",
          rule?.type || "",
          rule?.status || "",
          rule?.action?.mode || "",
          actionLabel[rule?.action?.mode] || "",
          ...conditionNames,
        ]
          .join(" ")
          .toLowerCase();

        return searchable.includes(query);
      });
    }

    return result;
  }, [rules, activeTab, search]);

  // ============================================================
  // CREATE RULE
  // ============================================================

  const handleCreateRule = () => {
    window.location.href = "/shipping_rule_edit?id=new";
  };

  // ============================================================
  // EDIT RULE
  // ============================================================

  const handleEditRule = (rule) => {
    if (!rule?.rule_id) {
      return;
    }

    window.location.href = `/shipping_rule_edit?id=${encodeURIComponent(
      rule.rule_id
    )}`;
  };

  // ============================================================
  // DRAG START
  // ============================================================

  const handleDragStart = (event, ruleId) => {
    /*
     * Reordering should only happen from the complete
     * "All" list without search/filter.
     */
    if (activeTab !== "all" || search.trim() || savingOrder) {
      event.preventDefault();

      return;
    }

    setDraggedRuleId(ruleId);

    setDropTargetId(null);

    event.dataTransfer.effectAllowed = "move";

    event.dataTransfer.setData("text/plain", ruleId);
  };

  // ============================================================
  // DRAG OVER
  // ============================================================

  const handleDragOver = (event, ruleId) => {
    event.preventDefault();

    if (!draggedRuleId || draggedRuleId === ruleId) {
      return;
    }

    event.dataTransfer.dropEffect = "move";

    setDropTargetId(ruleId);
  };

  // ============================================================
  // DROP
  // ============================================================

  const handleDrop = async (event, targetRuleId) => {
    event.preventDefault();

    const sourceRuleId =
      draggedRuleId || event.dataTransfer.getData("text/plain");

    setDropTargetId(null);

    if (!sourceRuleId || sourceRuleId === targetRuleId) {
      setDraggedRuleId(null);

      return;
    }

    /*
     * Do not reorder filtered lists.
     */
    if (activeTab !== "all" || search.trim()) {
      setDraggedRuleId(null);

      return;
    }

    const currentRules = [...rules];

    const sourceIndex = currentRules.findIndex(
      (item) => item?.rule_id === sourceRuleId
    );

    const targetIndex = currentRules.findIndex(
      (item) => item?.rule_id === targetRuleId
    );

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggedRuleId(null);

      return;
    }

    /*
     * Create new order.
     */
    const reorderedRules = [...currentRules];

    const [movedRule] = reorderedRules.splice(sourceIndex, 1);

    reorderedRules.splice(targetIndex, 0, movedRule);

    /*
     * Normalize evaluation order.
     */
    const normalizedRules = reorderedRules.map((rule, index) => ({
      ...rule,
      evaluation_order: index + 1,
    }));

    /*
     * Update UI immediately.
     */
    setRules(normalizedRules);

    setDraggedRuleId(null);

    try {
      setSavingOrder(true);

      const response = await axios.post(
        config.apiUrl + "/shipping_rule/reorder",
        {
          customer_id: userData.customer_id,

          rules: normalizedRules.map((rule) => ({
            rule_id: rule.rule_id,
          })),
        },
        {
          headers: config.headers,
        }
      );

      console.log("Shipping Rule Reorder Response =>", response.data);

      if (response.data?.status !== true) {
        throw new Error(
          response.data?.message || "Unable to save evaluation order."
        );
      }

      /*
       * Reload from DB.
       *
       * This guarantees the displayed order is
       * the persisted order.
       */
      await loadRules();

      /*
       * Small confirmation.
       */
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: response.data?.message || "Evaluation order updated.",
        showConfirmButton: false,
        timer: 1800,
      });
    } catch (error) {
      console.error(
        "Shipping Rule Reorder Error =>",
        error?.response?.data || error
      );

      /*
       * Restore database order.
       */
      await loadRules();

      Swal.fire({
        icon: "error",
        title: "Unable to reorder",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to save evaluation order.",
      });
    } finally {
      setSavingOrder(false);
    }
  };

  // ============================================================
  // DRAG END
  // ============================================================

  const handleDragEnd = () => {
    setDraggedRuleId(null);

    setDropTargetId(null);
  };

  // ============================================================
  // TOGGLE STATUS
  // ============================================================

  const updateStatus = async (rule, checked) => {
    if (!rule?.rule_id) {
      return;
    }

    const newStatus = checked ? "active" : "inactive";

    const oldStatus = String(rule?.status || "").toLowerCase();

    try {
      setUpdatingStatus(rule.rule_id);

      /*
       * Optimistic update.
       */
      setRules((oldRules) =>
        oldRules.map((item) =>
          item?.rule_id === rule.rule_id
            ? {
                ...item,
                status: newStatus,
              }
            : item
        )
      );

      const response = await axios.post(
        config.apiUrl + "/shipping_rule/toggle_status",
        {
          customer_id: userData.customer_id,

          rule_id: rule.rule_id,

          status: newStatus,
        },
        {
          headers: config.headers,
        }
      );

      console.log("Shipping Rule Toggle Response =>", response.data);

      if (response.data?.status !== true) {
        throw new Error(
          response.data?.message || "Unable to update rule status."
        );
      }

      /*
       * Reload to keep everything synchronized.
       */
      await loadRules();
    } catch (error) {
      console.error(
        "Shipping Rule Status Error =>",
        error?.response?.data || error
      );

      /*
       * Restore actual database value.
       */
      setRules((oldRules) =>
        oldRules.map((item) =>
          item?.rule_id === rule.rule_id
            ? {
                ...item,
                status: oldStatus,
              }
            : item
        )
      );

      Swal.fire({
        icon: "error",
        title: "Unable to update rule",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update rule status.",
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  // ============================================================
  // DELETE RULE
  // ============================================================

  const remove = async (rule) => {
    if (!rule?.rule_id) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete shipping rule?",
      text: `Are you sure you want to delete "${rule.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeletingRule(rule.rule_id);

      const response = await axios.post(
        config.apiUrl + "/shipping_rule/delete",
        {
          customer_id: userData.customer_id,

          rule_id: rule.rule_id,
        },
        {
          headers: config.headers,
        }
      );

      if (response.data?.status !== true) {
        throw new Error(response.data?.message || "Unable to delete rule.");
      }

      await loadRules();

      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: response.data?.message || "Shipping rule deleted.",
        showConfirmButton: false,
        timer: 1800,
      });
    } catch (error) {
      console.error(
        "Delete Shipping Rule Error =>",
        error?.response?.data || error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to delete rule",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    } finally {
      setDeletingRule(null);
    }
  };

  // ============================================================
  // DUPLICATE RULE
  // ============================================================

  const duplicate = async (rule) => {
    if (!rule?.rule_id) {
      return;
    }

    try {
      setDuplicatingRule(rule.rule_id);

      const response = await axios.post(
        config.apiUrl + "/shipping_rule/duplicate",
        {
          customer_id: userData.customer_id,

          rule_id: rule.rule_id,
        },
        {
          headers: config.headers,
        }
      );

      console.log("Duplicate Shipping Rule Response =>", response.data);

      if (response.data?.status !== true) {
        throw new Error(response.data?.message || "Unable to duplicate rule.");
      }

      await loadRules();

      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: response.data?.message || "Shipping rule duplicated.",
        showConfirmButton: false,
        timer: 1800,
      });
    } catch (error) {
      console.error(
        "Duplicate Shipping Rule Error =>",
        error?.response?.data || error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to duplicate rule",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    } finally {
      setDuplicatingRule(null);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="shipping-rules-page">
      <Header>
        <div className="content">
          {/* ======================================================
              PAGE HEADER
          ======================================================= */}

          <div className="sr-page-header">
            <div className="sr-page-heading">
              <h1 className="sr-page-title">Shipping Rules</h1>

              <div className="sr-page-subtitle">
                <span className="sr-sub-dot"></span>

                <span>Automation &amp; Rules</span>

                <span className="sr-sub-separator">•</span>

                <span>Tools</span>
              </div>
            </div>

            <button
              type="button"
              className="sr-create-btn"
              onClick={handleCreateRule}
            >
              <i className="bi bi-plus-lg"></i>

              <span>Add Rule</span>
            </button>
          </div>

          {/* ======================================================
              TOOLBAR
          ======================================================= */}

          <div className="sr-toolbar-card">
            <div className="sr-toolbar-top">
              <div className="sr-search-box">
                <i className="bi bi-search sr-search-icon"></i>

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search rule name, type, action..."
                  className="sr-search-input"
                />

                {search && (
                  <button
                    type="button"
                    className="sr-search-clear"
                    onClick={() => setSearch("")}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}

                <span className="sr-search-shortcut">⌘K / Ctrl K</span>
              </div>
            </div>

            {/* ==================================================
                TABS
            =================================================== */}

            <div className="sr-tabs">
              <button
                type="button"
                className={`sr-tab ${activeTab === "all" ? "active" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                <span>All</span>

                <span className="sr-count">{allCount}</span>
              </button>

              <button
                type="button"
                className={`sr-tab ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                <span>Active</span>

                <span className="sr-count">{activeCount}</span>
              </button>

              <button
                type="button"
                className={`sr-tab ${activeTab === "inactive" ? "active" : ""}`}
                onClick={() => setActiveTab("inactive")}
              >
                <span>Inactive</span>

                <span className="sr-count">{inactiveCount}</span>
              </button>
            </div>
          </div>

          {/* ======================================================
              TABLE CARD
          ======================================================= */}

          <div className="sr-table-card">
            <div className="sr-table-wrap">
              <table className="sr-table">
                <thead>
                  <tr>
                    <th className="sr-col-drag"></th>

                    <th className="sr-col-rule">Rule Name</th>

                    <th className="sr-col-type">Type</th>

                    <th className="sr-col-condition">Conditions</th>

                    <th className="sr-col-action">Action</th>

                    <th className="sr-col-updated">Updated</th>

                    <th className="sr-col-status">ON/OFF</th>

                    <th className="sr-col-actions"></th>
                  </tr>
                </thead>

                <tbody>
                  {/* =================================================
                      LOADING
                  ================================================== */}

                  {loading ? (
                    <tr>
                      <td colSpan="8" className="sr-loading-cell">
                        <div className="sr-loading">
                          <div className="sr-spinner"></div>

                          <span>Loading shipping rules...</span>
                        </div>
                      </td>
                    </tr>
                  ) : visibleRules.length === 0 ? (
                    /* ================================================
                       EMPTY
                    ================================================= */

                    <tr>
                      <td colSpan="8" className="sr-empty-cell">
                        <div className="sr-empty">
                          <div className="sr-empty-icon">
                            <i className="bi bi-journal-bookmark"></i>
                          </div>

                          {rules.length === 0 ? (
                            <>
                              <h3>No shipping rules yet</h3>

                              <p>
                                Create your first rule to automatically assign
                                couriers based on payment mode, weight, pincode,
                                zone, state or SKUs.
                              </p>

                              <button
                                type="button"
                                className="sr-empty-create-btn"
                                onClick={handleCreateRule}
                              >
                                <i className="bi bi-plus-lg"></i>

                                <span>Create your first rule</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <h3>No matching rules</h3>

                              <p>
                                No shipping rules match your current search or
                                selected filter.
                              </p>

                              <button
                                type="button"
                                className="sr-empty-clear-btn"
                                onClick={() => {
                                  setSearch("");
                                  setActiveTab("all");
                                }}
                              >
                                Clear filters
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    /* =================================================
                       RULES
                    ================================================== */

                    visibleRules.map((rule) => {
                      const globalIndex = rules.findIndex(
                        (item) => item?.rule_id === rule?.rule_id
                      );

                      const rank =
                        globalIndex >= 0
                          ? globalIndex + 1
                          : Number(rule?.evaluation_order) || 1;

                      const conditionCount = Array.isArray(rule?.conditions)
                        ? rule.conditions.length
                        : 0;

                      const courierCount = Array.isArray(rule?.action?.couriers)
                        ? rule.action.couriers.length
                        : 0;

                      const mode = rule?.action?.mode || "custom";

                      const isActive =
                        String(rule?.status || "").toLowerCase() === "active";

                      const isDragging = draggedRuleId === rule?.rule_id;

                      const isDropTarget = dropTargetId === rule?.rule_id;

                      const isUpdating = updatingStatus === rule?.rule_id;

                      const isDeleting = deletingRule === rule?.rule_id;

                      const isDuplicating = duplicatingRule === rule?.rule_id;

                      return (
                        <tr
                          key={rule?.rule_id}
                          className={`
                              sr-rule-row
                              ${isDragging ? "sr-row-dragging" : ""}
                              ${isDropTarget ? "sr-row-drop-target" : ""}
                            `}
                          draggable={
                            activeTab === "all" &&
                            !search.trim() &&
                            !savingOrder
                          }
                          onDragStart={(event) =>
                            handleDragStart(event, rule?.rule_id)
                          }
                          onDragOver={(event) =>
                            handleDragOver(event, rule?.rule_id)
                          }
                          onDrop={(event) => handleDrop(event, rule?.rule_id)}
                          onDragEnd={handleDragEnd}
                        >
                          {/* ======================================
                                DRAG
                            ======================================= */}

                          <td className="sr-drag-td">
                            <i className="bi bi-grip-vertical sr-grip"></i>
                          </td>

                          {/* ======================================
                                NAME
                            ======================================= */}

                          <td>
                            <div className="sr-name-cell">
                              <span className="sr-rank">{rank}</span>

                              <div className="sr-name-content">
                                <div className="sr-name-main">
                                  {rule?.name || "Untitled Rule"}
                                </div>

                                <div className="sr-name-sub">
                                  {rule?.type || "Domestic"} · #{rank} in
                                  evaluation order
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* ======================================
                                TYPE
                            ======================================= */}

                          <td>
                            <span
                              className={`sr-type-badge ${
                                typeClass[rule?.type] || "custom"
                              }`}
                            >
                              {rule?.type || "Custom"}
                            </span>
                          </td>

                          {/* ======================================
                                CONDITIONS
                            ======================================= */}

                          <td>
                            {conditionCount === 0 ? (
                              <span className="sr-condition-none">
                                None
                                <small>match all</small>
                              </span>
                            ) : (
                              <span className="sr-condition-count">
                                {conditionCount}
                                <span>/7</span>
                              </span>
                            )}
                          </td>

                          {/* ======================================
                                ACTION
                            ======================================= */}

                          <td>
                            <div className="sr-action-cell">
                              <span
                                className={`sr-action-badge sr-action-${mode}`}
                              >
                                {actionLabel[mode] || "Custom"}
                              </span>

                              <span className="sr-action-sub">
                                {mode === "custom"
                                  ? `${courierCount} courier${
                                      courierCount === 1 ? "" : "s"
                                    }`
                                  : "system ranked"}
                              </span>
                            </div>
                          </td>

                          {/* ======================================
                                UPDATED
                            ======================================= */}

                          <td>
                            <span className="sr-updated">
                              {relTime(rule?.updated_at || rule?.updatedAt)}
                            </span>
                          </td>

                          {/* ======================================
                                TOGGLE
                            ======================================= */}

                          <td className="sr-status-td">
                            <label
                              className={`sr-switch ${
                                isUpdating ? "loading" : ""
                              }`}
                              title={
                                isActive ? "Deactivate rule" : "Activate rule"
                              }
                            >
                              <input
                                type="checkbox"
                                checked={isActive}
                                disabled={isUpdating}
                                onChange={(event) =>
                                  updateStatus(rule, event.target.checked)
                                }
                              />

                              <span className="sr-switch-slider"></span>
                            </label>
                          </td>

                          {/* ======================================
                                ACTIONS
                            ======================================= */}

                          <td className="sr-actions-td">
                            <div className="sr-row-actions">
                              <button
                                type="button"
                                className="sr-row-btn"
                                title="Edit rule"
                                onClick={() => handleEditRule(rule)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>

                              <button
                                type="button"
                                className="sr-row-btn"
                                title="Duplicate rule"
                                disabled={isDuplicating}
                                onClick={() => duplicate(rule)}
                              >
                                {isDuplicating ? (
                                  <span className="sr-mini-spinner"></span>
                                ) : (
                                  <i className="bi bi-copy"></i>
                                )}
                              </button>

                              <button
                                type="button"
                                className="sr-row-btn danger"
                                title="Delete rule"
                                disabled={isDeleting}
                                onClick={() => remove(rule)}
                              >
                                {isDeleting ? (
                                  <span className="sr-mini-spinner"></span>
                                ) : (
                                  <i className="bi bi-trash3"></i>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ======================================================
              REORDER INFORMATION
          ======================================================= */}

          {rules.length > 1 && activeTab === "all" && !search.trim() && (
            <div className="sr-order-hint">
              <i className="bi bi-arrows-move"></i>

              <span>
                Drag and drop rules to change their evaluation order. Rules are
                evaluated from top to bottom.
              </span>

              {savingOrder && (
                <span className="sr-saving-order">Saving order...</span>
              )}
            </div>
          )}
        </div>
      </Header>

      <Footer />

      {/* ============================================================
          PAGE CSS
      ============================================================= */}

      <style>{`
        .shipping-rules-page {
          width: 100%;
          min-height: 100%;
          color: #18202f;
        }

        .shipping-rules-page *,
        .shipping-rules-page *::before,
        .shipping-rules-page *::after {
          box-sizing: border-box;
        }

        /* ==========================================================
           PAGE HEADER
        =========================================================== */

        .sr-page-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          margin-bottom: 28px;
        }

        .sr-page-heading {
          min-width: 0;
        }

        .sr-page-title {
          margin: 0;
          color: #18202f;
          font-size: 30px;
          line-height: 1.15;
          font-weight: 750;
          letter-spacing: -0.7px;
        }

        .sr-page-subtitle {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 9px;
          color: #98a2b3;
          font-size: 15px;
          line-height: 1.4;
          font-weight: 500;
        }

        .sr-sub-dot {
          width: 7px;
          height: 7px;
          min-width: 7px;
          border-radius: 50%;
          background: #12a36a;
        }

        .sr-sub-separator {
          color: #c6ccd5;
        }

        .sr-create-btn {
          height: 52px;
          padding: 0 24px;
          border: 0;
          border-radius: 14px;
          outline: none;
          background: linear-gradient(
            135deg,
            #4436a8,
            #6551cf
          );
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 700;
          white-space: nowrap;
          cursor: pointer;
          box-shadow:
            0 10px 22px
              rgba(75, 57, 175, 0.2);
          transition:
            transform 0.18s ease,
            box-shadow 0.18s ease;
        }

        .sr-create-btn:hover {
          transform: translateY(-1px);
          box-shadow:
            0 13px 27px
              rgba(75, 57, 175, 0.25);
        }

        .sr-create-btn i {
          font-size: 17px;
        }

        /* ==========================================================
           TOOLBAR
        =========================================================== */

        .sr-toolbar-card {
          width: 100%;
          background: #ffffff;
          border: 1px solid #edf0f4;
          border-radius: 18px;
          padding: 20px 22px 16px;
          margin-bottom: 24px;
          box-shadow:
            0 8px 25px
              rgba(20, 30, 50, 0.045);
        }

        .sr-toolbar-top {
          width: 100%;
        }

        .sr-search-box {
          width: min(620px, 100%);
          height: 44px;
          border: 1px solid #dfe3ea;
          border-radius: 10px;
          background: #ffffff;
          display: flex;
          align-items: center;
          position: relative;
          transition:
            border-color 0.15s ease,
            box-shadow 0.15s ease;
        }

        .sr-search-box:focus-within {
          border-color: #6653d0;
          box-shadow:
            0 0 0 3px
              rgba(102, 83, 208, 0.09);
        }

        .sr-search-icon {
          width: 42px;
          min-width: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8d97a8;
          font-size: 16px;
        }

        .sr-search-input {
          flex: 1;
          min-width: 0;
          height: 100%;
          padding: 0 8px 0 0 !important;
          border: 0 !important;
          outline: none !important;
          background: transparent !important;
          box-shadow: none !important;
          color: #1d2635;
          font-size: 14px;
        }

        .sr-search-input::placeholder {
          color: #9aa3b2;
          opacity: 1;
        }

        .sr-search-shortcut {
          flex: 0 0 auto;
          margin-right: 10px;
          padding: 4px 7px;
          border: 1px solid #e2e5eb;
          border-radius: 6px;
          background: #f8f9fb;
          color: #8b94a3;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }

        .sr-search-clear {
          width: 28px;
          height: 28px;
          margin-right: 4px;
          padding: 0;
          border: 0;
          border-radius: 7px;
          background: transparent;
          color: #9aa2af;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .sr-search-clear:hover {
          background: #f1f2f5;
          color: #4f5664;
        }

        /* ==========================================================
           TABS
        =========================================================== */

        .sr-tabs {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 14px;
        }

        .sr-tab {
          height: 38px;
          padding: 0 13px;
          border: 1px solid #e5e8ed;
          border-radius: 10px;
          background: #ffffff;
          color: #596476;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .sr-tab:hover {
          border-color: #cfd4dd;
          background: #fafbfc;
        }

        .sr-tab.active {
          background: #4a3aab;
          border-color: #4a3aab;
          color: #ffffff;
          box-shadow:
            0 7px 16px
              rgba(74, 58, 171, 0.17);
        }

        .sr-count {
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 7px;
          background: #f0f2f6;
          color: #8b94a3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 800;
        }

        .sr-tab.active .sr-count {
          background: rgba(
            255,
            255,
            255,
            0.16
          );
          color: #ffffff;
        }

        /* ==========================================================
           TABLE CARD
        =========================================================== */

        .sr-table-card {
          width: 100%;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid #edf0f4;
          border-radius: 18px;
          box-shadow:
            0 8px 25px
              rgba(20, 30, 50, 0.045);
        }

        .sr-table-wrap {
          width: 100%;
          overflow-x: auto;
        }

        .sr-table {
          width: 100%;
          min-width: 1080px;
          border-collapse: separate;
          border-spacing: 0;
          table-layout: fixed;
        }

        .sr-table thead th {
          height: 58px;
          padding: 0 16px;
          border-bottom: 1px solid #e8ebef;
          background: #ffffff;
          color: #98a1b1;
          font-size: 12px;
          line-height: 1;
          font-weight: 800;
          text-align: left;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }

        .sr-table tbody td {
          height: 74px;
          padding: 14px 16px;
          border-bottom: 1px solid #f0f2f5;
          color: #283244;
          font-size: 13px;
          vertical-align: middle;
          background: #ffffff;
        }

        .sr-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .sr-rule-row {
          transition:
            opacity 0.15s ease,
            background 0.15s ease;
        }

        .sr-rule-row[draggable="true"] {
          cursor: grab;
        }

        .sr-rule-row[draggable="true"]:active {
          cursor: grabbing;
        }

        .sr-rule-row:hover td {
          background: #fcfcfe;
        }

        .sr-row-dragging {
          opacity: 0.45;
        }

        .sr-row-drop-target td {
          background: #f5f2ff !important;
        }

        .sr-row-drop-target td:first-child {
          box-shadow:
            inset 3px 0 0 #5744bd;
        }

        /* ==========================================================
           TABLE WIDTHS
        =========================================================== */

        .sr-col-drag {
          width: 42px;
        }

        .sr-col-rule {
          width: 25%;
        }

        .sr-col-type {
          width: 12%;
        }

        .sr-col-condition {
          width: 14%;
        }

        .sr-col-action {
          width: 15%;
        }

        .sr-col-updated {
          width: 12%;
        }

        .sr-col-status {
          width: 9%;
          text-align: center !important;
        }

        .sr-col-actions {
          width: 105px;
        }

        .sr-drag-td {
          width: 42px;
          padding-left: 10px !important;
          padding-right: 4px !important;
          text-align: center;
        }

        .sr-grip {
          color: #b1b8c4;
          font-size: 17px;
          cursor: grab;
        }

        .sr-rule-row:hover .sr-grip {
          color: #6653c8;
        }

        /* ==========================================================
           NAME
        =========================================================== */

        .sr-name-cell {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .sr-rank {
          width: 30px;
          height: 30px;
          min-width: 30px;
          border-radius: 8px;
          background: #f0edff;
          color: #5745b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
        }

        .sr-name-content {
          min-width: 0;
        }

        .sr-name-main {
          display: block;
          overflow: hidden;
          color: #1d2635;
          font-size: 14px;
          font-weight: 700;
          line-height: 1.35;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .sr-name-sub {
          display: block;
          overflow: hidden;
          margin-top: 3px;
          color: #98a1b0;
          font-size: 11px;
          line-height: 1.3;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ==========================================================
           TYPE BADGES
        =========================================================== */

        .sr-type-badge {
          min-height: 28px;
          padding: 0 10px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
        }

        .sr-type-badge.domestic {
          background: #edf4ff;
          color: #416bb2;
        }

        .sr-type-badge.international {
          background: #f3edff;
          color: #7655b8;
        }

        .sr-type-badge.cod {
          background: #fff2e8;
          color: #bc6a2a;
        }

        .sr-type-badge.prepaid {
          background: #eaf8f1;
          color: #16845a;
        }

        .sr-type-badge.b2b {
          background: #eef2f7;
          color: #596579;
        }

        .sr-type-badge.custom {
          background: #f1edff;
          color: #604bbd;
        }

        /* ==========================================================
           CONDITIONS
        =========================================================== */

        .sr-condition-count {
          color: #4a5363;
          font-weight: 800;
        }

        .sr-condition-count span {
          color: #a0a7b3;
          font-weight: 600;
        }

        .sr-condition-none {
          display: inline-flex;
          flex-direction: column;
          color: #697386;
          font-size: 12px;
          font-weight: 600;
        }

        .sr-condition-none small {
          margin-top: 2px;
          color: #a2a9b4;
          font-size: 10px;
          font-weight: 500;
        }

        /* ==========================================================
           ACTION
        =========================================================== */

        .sr-action-cell {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
        }

        .sr-action-badge {
          min-height: 27px;
          padding: 0 10px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
        }

        .sr-action-custom {
          background: #f0edff;
          color: #604bbd;
        }

        .sr-action-fastest {
          background: #fff4dc;
          color: #ad7515;
        }

        .sr-action-cheapest {
          background: #eaf8f1;
          color: #16845a;
        }

        .sr-action-sub {
          color: #9ba3af;
          font-size: 10px;
          line-height: 1.2;
        }

        .sr-updated {
          color: #7e8796;
          font-size: 12px;
        }

        /* ==========================================================
           TOGGLE
        =========================================================== */

        .sr-status-td {
          text-align: center !important;
        }

        .sr-switch {
          width: 40px;
          height: 22px;
          display: inline-block;
          position: relative;
          cursor: pointer;
          vertical-align: middle;
        }

        .sr-switch.loading {
          opacity: 0.5;
          pointer-events: none;
        }

        .sr-switch input {
          position: absolute;
          width: 1px;
          height: 1px;
          opacity: 0;
          pointer-events: none;
        }

        .sr-switch-slider {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          background: #d8dce3;
          transition: 0.18s ease;
        }

        .sr-switch-slider::before {
          content: "";
          position: absolute;
          width: 16px;
          height: 16px;
          top: 3px;
          left: 3px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow:
            0 1px 3px
              rgba(0, 0, 0, 0.18);
          transition: 0.18s ease;
        }

        .sr-switch input:checked
          + .sr-switch-slider {
          background: #4f3bb1;
        }

        .sr-switch input:checked
          + .sr-switch-slider::before {
          transform: translateX(18px);
        }

        /* ==========================================================
           ROW ACTIONS
        =========================================================== */

        .sr-actions-td {
          text-align: right !important;
        }

        .sr-row-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 4px;
        }

        .sr-row-btn {
          width: 32px;
          height: 32px;
          padding: 0;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: #8992a1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition:
            background 0.15s ease,
            color 0.15s ease;
        }

        .sr-row-btn:hover {
          background: #f1f2f5;
          color: #4f3bb1;
        }

        .sr-row-btn.danger:hover {
          background: #fff0f0;
          color: #d94b4b;
        }

        .sr-row-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        /* ==========================================================
           LOADING
        =========================================================== */

        .sr-loading-cell {
          height: 420px;
          border-bottom: 0 !important;
        }

        .sr-loading {
          min-height: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: #8c95a4;
          font-size: 13px;
        }

        .sr-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #e7e8ef;
          border-top-color: #5543b5;
          border-radius: 50%;
          animation: srSpin 0.8s linear infinite;
        }

        .sr-mini-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid #e1e3e8;
          border-top-color: #5b48bb;
          border-radius: 50%;
          animation: srSpin 0.7s linear infinite;
        }

        @keyframes srSpin {
          to {
            transform: rotate(360deg);
          }
        }

        /* ==========================================================
           EMPTY
        =========================================================== */

        .sr-empty-cell {
          height: 470px;
          padding: 0 !important;
          border-bottom: 0 !important;
        }

        .sr-empty {
          width: 100%;
          height: 470px;
          padding: 50px 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .sr-empty-icon {
          width: 88px;
          height: 88px;
          margin-bottom: 22px;
          border-radius: 50%;
          background: #eeefff;
          color: #4e3eaf;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sr-empty-icon i {
          font-size: 38px;
        }

        .sr-empty h3 {
          margin: 0;
          color: #18202f;
          font-size: 23px;
          line-height: 1.25;
          font-weight: 750;
        }

        .sr-empty p {
          max-width: 570px;
          margin: 12px auto 25px;
          color: #667085;
          font-size: 15px;
          line-height: 1.65;
        }

        .sr-empty-create-btn {
          height: 48px;
          padding: 0 20px;
          border: 0;
          border-radius: 12px;
          background: linear-gradient(
            135deg,
            #4436a8,
            #6551cf
          );
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          box-shadow:
            0 9px 18px
              rgba(75, 57, 175, 0.18);
        }

        .sr-empty-create-btn:hover {
          box-shadow:
            0 11px 22px
              rgba(75, 57, 175, 0.24);
        }

        .sr-empty-clear-btn {
          height: 42px;
          padding: 0 18px;
          border: 1px solid #dddfe6;
          border-radius: 10px;
          background: #ffffff;
          color: #5544b2;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        .sr-empty-clear-btn:hover {
          background: #f7f6ff;
          border-color: #cfc8f1;
        }

        /* ==========================================================
           ORDER HINT
        =========================================================== */

        .sr-order-hint {
          min-height: 42px;
          margin-top: 12px;
          padding: 0 14px;
          border: 1px solid #e8e5f8;
          border-radius: 10px;
          background: #faf9ff;
          color: #7a7397;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
        }

        .sr-order-hint i {
          color: #5a48b8;
          font-size: 14px;
        }

        .sr-saving-order {
          margin-left: auto;
          color: #5544b2;
          font-weight: 700;
        }

        /* ==========================================================
           RESPONSIVE
        =========================================================== */

        @media (max-width: 900px) {
          .sr-page-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .sr-create-btn {
            width: 100%;
          }

          .sr-search-box {
            width: 100%;
          }
        }

        @media (max-width: 768px) {
          .sr-toolbar-card {
            padding: 16px;
          }

          .sr-empty,
          .sr-empty-cell {
            height: 400px;
          }

          .sr-empty p {
            font-size: 14px;
          }

          .sr-search-shortcut {
            display: none;
          }

          .sr-order-hint {
            align-items: flex-start;
            padding: 10px 12px;
          }

          .sr-saving-order {
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Shipping_rules;
